import { Texture } from "pixi.js";

/** The lettering on the Secret Bonus disc: the art has none; the layout example letters it in dark bold capitals. */
const LETTERING = {
  color: "#2a1608",
  font: "Roboto, system-ui, sans-serif",
  weight: 700,
  /** The font size before fitting, as a share of the disc's width. */
  sizeRatio: 0.2,
  /** The widest line fills at most this share of the disc's width. */
  maxWidthRatio: 0.74,
  /** The lines' block is centred this far down the image: the disc's face, above the shadow of its rim. */
  centerYRatio: 0.47,
  /** The line height as a multiple of the font size. */
  lineHeight: 1.08,
} as const;

/**
 * The Secret Bonus symbol's texture: the delivery's gold disc with its lettering drawn on in the player's language, once
 * at boot on a 2D canvas, so the cells, the pool and the merge draw it as they draw every symbol. The lines shrink
 * together to fit the disc, so a longer translation draws smaller. The shell's Roboto is loaded before the scene.
 */
export function drawSecretBonusSymbol(disc: Texture, label: string): Texture {
  const source = disc.source;
  const width = source.pixelWidth;
  const height = source.pixelHeight;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return disc;
  context.drawImage(source.resource as CanvasImageSource, 0, 0, width, height);
  const lines = label.split("\n");
  const fontAt = (px: number): string => `${LETTERING.weight} ${px}px ${LETTERING.font}`;
  let size = Math.round(width * LETTERING.sizeRatio);
  context.font = fontAt(size);
  const widest = Math.max(...lines.map((line) => context.measureText(line).width));
  const maxWidth = width * LETTERING.maxWidthRatio;
  if (widest > maxWidth) size = Math.floor((size * maxWidth) / widest);
  context.font = fontAt(size);
  context.fillStyle = LETTERING.color;
  context.textAlign = "center";
  context.textBaseline = "middle";
  const step = size * LETTERING.lineHeight;
  const top = height * LETTERING.centerYRatio - (step * (lines.length - 1)) / 2;
  lines.forEach((line, i) => context.fillText(line, width / 2, top + i * step));
  return Texture.from(canvas);
}
